import {
  DateRangePreset,
  DateRangeValue,
} from '@app/components/date-range-selector/date-range-presets';
import { useTimezone } from '@app/core/hooks/useTimezone';
import { Box, Skeleton, useTheme } from '@mui/material';
import { NullState } from '@ui/components';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { AlignedData, Options } from 'uplot';
import { useYoutubeStatsSettingsStore } from '../../../../../stores/youtube-stats-settings.store';
import { pastelColors } from '../../../transcriptions/utils';
import { YoutubeStatsLineChart } from '../charts/YoutubeStatsLineChart';
import {
  buildYoutubeChannelLegendMeta,
  transformViewersStatsForUplot,
  ViewersStatsForChart,
} from '../utils/transform-viewers-stats-for-uplot';
import { YoutubeStatsChartFetchOverlay } from './YoutubeStatsChartFetchOverlay';
import { YoutubeStatsCustomLegend } from './YoutubeStatsCustomLegend';

/** Horizontal space reserved for the custom legend beside the chart. */
const YOUTUBE_STATS_LEGEND_WIDTH = 320;

const noDataMessage = defineMessage({
  id: 'logger.youtubeStats.noDataForPeriod',
  defaultMessage: 'No hay datos disponibles para este período',
});

export type YoutubeStatsViewV2Props = {
  stats: ViewersStatsForChart | undefined;
  isLoading: boolean;
  /** True while the BFF stats request is in flight (including background refetch). */
  isStatsFetching: boolean;
  stationYoutubeChannelId?: string | null;
  dateRange: DateRangeValue;
  dateRangePreset: DateRangePreset;
  loggerDateRange: DateRangeValue | null;
  handleDateRangeChange: (range: DateRangeValue, preset: DateRangePreset) => void;
  handleDateRangeReset: () => void;
};

function areDateRangesEqual(left: DateRangeValue, right: DateRangeValue): boolean {
  return left.from.getTime() === right.from.getTime() && left.to.getTime() === right.to.getTime();
}

/**
 * YouTube viewer chart fed by the BFF country catalog payload. Series visibility is derived locally so
 * changing scope or selected channels does not refetch.
 */
export function YoutubeStatsViewV2(props: YoutubeStatsViewV2Props) {
  const {
    stats,
    isLoading,
    isStatsFetching,
    dateRange,
    dateRangePreset,
    loggerDateRange,
    handleDateRangeChange,
    handleDateRangeReset,
    stationYoutubeChannelId,
  } = props;

  const { formatMessage } = useIntl();
  const tz = useTimezone();
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const axisStroke = isDark ? '#e2e8f0' : '#333';
  const gridStroke = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const selectedChannelIds = useYoutubeStatsSettingsStore((state) => state.selectedChannelIds);
  const toggleChannel = useYoutubeStatsSettingsStore((state) => state.toggleChannel);
  const selectChannels = useYoutubeStatsSettingsStore((state) => state.selectChannels);
  const clearChannels = useYoutubeStatsSettingsStore((state) => state.clearChannels);
  const syncAvailableChannels = useYoutubeStatsSettingsStore(
    (state) => state.syncAvailableChannels,
  );

  const availableChannelMeta = useMemo(
    () => buildYoutubeChannelLegendMeta(stats, pastelColors),
    [stats],
  );
  const availableChannelIds = useMemo(
    () => availableChannelMeta.map((channel) => channel.channelId),
    [availableChannelMeta],
  );
  const selectedChannelSet = useMemo(() => new Set(selectedChannelIds), [selectedChannelIds]);

  const ownChannelId = useMemo(() => {
    if (!availableChannelMeta.length) {
      return null;
    }

    const ownFromMeta = availableChannelMeta.find((item) => item.isSelf)?.channelId;
    if (ownFromMeta) {
      return ownFromMeta;
    }

    if (stationYoutubeChannelId && availableChannelIds.includes(stationYoutubeChannelId)) {
      return stationYoutubeChannelId;
    }

    return null;
  }, [availableChannelIds, availableChannelMeta, stationYoutubeChannelId]);

  const visibleChannelIds = useMemo(() => {
    return availableChannelIds.filter((channelId) => selectedChannelSet.has(channelId));
  }, [availableChannelIds, selectedChannelSet]);

  const previousOwnChannelIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ownChannelId) {
      previousOwnChannelIdRef.current = null;
      return;
    }

    const isNewStationLinkedChannel = previousOwnChannelIdRef.current !== ownChannelId;
    if (isNewStationLinkedChannel && !selectedChannelSet.has(ownChannelId)) {
      selectChannels([ownChannelId]);
    }

    previousOwnChannelIdRef.current = ownChannelId;
  }, [ownChannelId, selectChannels, selectedChannelSet]);

  const transformedData = useMemo(() => {
    return transformViewersStatsForUplot(stats, visibleChannelIds, tz, pastelColors);
  }, [stats, visibleChannelIds, tz]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [divWidth, setDivWidth] = useState(0);
  const [legendDateRange, setLegendDateRange] = useState<DateRangeValue>(dateRange);
  const [legendDateRangePreset, setLegendDateRangePreset] =
    useState<DateRangePreset>(dateRangePreset);

  useEffect(() => {
    if (containerRef.current) {
      setDivWidth(containerRef.current.offsetWidth);
    }
  }, []);
  useEffect(() => {
    if (availableChannelIds.length > 0) {
      syncAvailableChannels(availableChannelIds);
    }
  }, [availableChannelIds, syncAvailableChannels]);
  useEffect(() => {
    setLegendDateRange(dateRange);
    setLegendDateRangePreset(dateRangePreset);
  }, [dateRange, dateRangePreset]);

  const chartWidth = Math.max(0, divWidth - YOUTUBE_STATS_LEGEND_WIDTH - 16);
  const legendItems = useMemo(() => {
    return availableChannelMeta.map((channel) => ({
      channelId: channel.channelId,
      label: channel.label,
      thumbnailUrl: channel.thumbnailUrl,
      color: channel.color,
      checked: selectedChannelSet.has(channel.channelId),
    }));
  }, [availableChannelMeta, selectedChannelSet]);
  const otherChannelIds = useMemo(
    () => availableChannelIds.filter((channelId) => channelId !== ownChannelId),
    [availableChannelIds, ownChannelId],
  );
  const isDateRangeResetDisabled = useMemo(() => {
    if (!loggerDateRange) {
      return true;
    }

    return (
      legendDateRangePreset === 'customRange' &&
      areDateRangesEqual(legendDateRange, loggerDateRange)
    );
  }, [legendDateRange, legendDateRangePreset, loggerDateRange]);

  const handleVisibleRangeChange = useCallback((fromUnix: number, toUnix: number) => {
    const from = new Date(Math.min(fromUnix, toUnix) * 1000);
    const to = new Date(Math.max(fromUnix, toUnix) * 1000);
    setLegendDateRange({ from, to });
    setLegendDateRangePreset('customRange');
  }, []);

  const opts: Options = useMemo(() => {
    return {
      width: chartWidth,
      height: 640,
      series: [{}, ...transformedData.series],
      cursor: {
        points: {
          size: 12,
        },
        focus: {
          prox: 6,
          alpha: 0.4,
        },
        drag: {
          x: true,
          y: false,
          setScale: true,
        },
      },
      hooks: {
        setSelect: [
          (u) => {
            const { left, width } = u.select;
            if (!width || width < 2) {
              return;
            }

            const fromUnix = u.posToVal(left, 'x');
            const toUnix = u.posToVal(left + width, 'x');

            if (
              fromUnix == null ||
              toUnix == null ||
              !Number.isFinite(fromUnix) ||
              !Number.isFinite(toUnix)
            ) {
              return;
            }

            handleVisibleRangeChange(fromUnix, toUnix);
          },
        ],
      },
      legend: {
        show: false,
      },
      axes: [
        {
          space: 40,
          stroke: axisStroke,
          incrs: [1, 5, 10, 15, 30, 60, 60 * 2, 60 * 3, 60 * 6, 60 * 12, 60 * 24, 60 * 48],
          values: (u, vals) => {
            return vals.map((v) => {
              const momentTime = moment.unix(v).tz(tz);
              return momentTime.format('HH:mm');
            });
          },
          grid: { stroke: gridStroke, width: 1 },
          ticks: { stroke: gridStroke, width: 1 },
        },
        {
          space: 40,
          size: 70,
          stroke: axisStroke,
          grid: { stroke: gridStroke, width: 1 },
          ticks: { stroke: gridStroke, width: 1 },
        },
      ],
    };
  }, [chartWidth, handleVisibleRangeChange, transformedData.series, tz]);

  const hasSeriesData = Boolean(stats?.timeSeries?.length) && availableChannelIds.length > 0;

  const showInitialSkeleton = isLoading && !stats;

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        gap: 2,
        mb: 2,
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      {showInitialSkeleton ? (
        <Box>
          <Skeleton variant="rectangular" width="100%" height={640} />
        </Box>
      ) : hasSeriesData ? (
        <Box sx={{ position: 'relative', minHeight: 640 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <YoutubeStatsCustomLegend
              items={legendItems}
              ownChannelId={ownChannelId}
              dateRange={legendDateRange}
              dateRangePreset={legendDateRangePreset}
              onDateRangeChange={handleDateRangeChange}
              onDateRangeReset={handleDateRangeReset}
              isDateRangeResetDisabled={isDateRangeResetDisabled}
              onToggleChannel={toggleChannel}
              onSelectAllOthers={() => selectChannels(otherChannelIds)}
              onSelectNoneOthers={() => clearChannels(otherChannelIds)}
            />
            <YoutubeStatsLineChart
              options={opts}
              data={transformedData.data as AlignedData}
              stats={stats}
              seriesChannelIds={transformedData.seriesChannelIds}
              timezone={tz}
            />
          </Box>
          {isStatsFetching ? <YoutubeStatsChartFetchOverlay /> : null}
        </Box>
      ) : (
        <NullState className="p-7">{formatMessage(noDataMessage)}</NullState>
      )}
    </Box>
  );
}
